"use client";

import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useMutation } from "@tanstack/react-query";
import {
  type AnonymousEcc,
  type AnonymousFormat,
  type AnonymousFrame,
  type AnonymousPayload,
  AnonymousPayloadSchema,
  downloadBlob,
  renderAnonymous,
} from "@qrody/api-client/render";
import {
  buildMatrixWithMeta,
  computeMatrixModules,
  countActiveVanityFeatures,
  isEccUsable,
  renderClient,
} from "@qrody/qr-renderer";
import type { QrMode } from "@qrody/api-client/codes";
import type { GeneratorCapabilities } from "../domain/capabilities";
import { buildSaveInput } from "../domain/save-mapping";
import {
  buildPayload,
  isDynamicCompatible,
  MM_PER_INCH,
  PLACEHOLDER_PAYLOAD,
  PX_PER_MODULE_GREEN,
  zodPathToFieldId,
} from "../domain/payload";
import {
  INITIAL_FORM,
  type PayloadType,
  type QrGeneratorFormState,
  type QrGeneratorSeed,
} from "../domain/seed";
import { saveDraft } from "../state/draft-bridge";
import { useActionHandlers } from "../state/use-action-handlers";
import { useErrMessage } from "../state/use-err-message";
import { RENDER_SIZE_MAX_PX, RENDER_SIZE_MIN_PX } from "@qrody/api-client/render";
import { ContentPanel } from "../panels/content-panel";
import { PreviewPanel } from "../panels/preview-panel";
import { DesignPanel } from "../panels/design-panel";
import { VanityUpgradeDialog } from "./vanity-upgrade-dialog";

/**
 * Snapshot of the generator's current edit state — handed to a custom
 * `actionSlot` so callers (the `/codes/{id}` edit dialog) can build their
 * own primary action button (Save / Update / etc.) on top of the shared
 * editor surface.
 */
export interface QrGeneratorState {
  type: PayloadType;
  /**
   * Live snapshot of the editable form fields. Mirrors the internal
   * `FormState` 1:1 so callers (e.g. the anonymous→authenticated draft
   * bridge) can persist what the user has typed even when it currently
   * fails schema validation.
   */
  form: QrGeneratorFormState;
  payload: AnonymousPayload | null;
  design: {
    foreground: string;
    background: string;
    frame: AnonymousFrame;
    ecc: AnonymousEcc;
    minVersion: number | null;
    /** Default download format — persisted on save so the editor
     *  restores it when the code is reopened. */
    format: AnonymousFormat;
    /** Default raster pixel side-length — same persistence semantics
     *  as `format`. */
    size: number;
  };
  /**
   * False when the live form fails the AnonymousPayloadSchema (e.g. URL
   * field has no scheme). The caller-supplied save action is expected to
   * disable itself in that case so we don't persist a code that won't
   * scan on a phone — the backend tolerates arbitrary strings.
   */
  validationOk: boolean;
  /**
   * Human-readable description of the first live-validation issue.
   * Null when the form is valid.
   */
  validationMessage: string | null;
  /**
   * The mode the editor is currently set to — `DYNAMIC` for slug-based
   * redirects, `STATIC` for matrices that bake the payload directly.
   * Reflects the user's pick on the Code-Modus toggle when the payload
   * is dynamic-compatible; for WIFI/EMAIL/SMS etc. the toggle is hidden
   * and this collapses to `STATIC` (the only thing the backend will
   * accept for those types). Save-action consumers must forward this
   * as `forceMode` to `buildSaveInput` — otherwise the wire body
   * silently falls back to DYNAMIC, the original cause of the
   * `qr.dynamic.max_active` 402 even with the radio set to Static.
   */
  mode: QrMode;
}

/** Alias for the action-slot snapshot — used by panels/design-panel. */
export type QrGeneratorActionState = QrGeneratorState;

export type { QrGeneratorSeed };

export interface QrGeneratorProps {
  caps: GeneratorCapabilities;
  seed?: QrGeneratorSeed;
  disableTypeChange?: boolean;
  disablePayloadFields?: boolean;
  disableEcc?: boolean;
  /**
   * Override the payload that the live preview encodes into the QR
   * matrix. Used by the `/codes/{id}` edit dialog for DYNAMIC codes —
   * the preview must show the slug-redirect URL (what's actually printed
   * on paper) rather than the form's target URL (which is just the
   * editable redirect destination behind the slug).
   */
  previewPayloadOverride?: AnonymousPayload;
  /** Replace the built-in download/save action bar with caller-supplied UI. */
  actionSlot?: (state: QrGeneratorState) => ReactNode;
  /** Subscribe to live editor state. */
  onStateChange?: (state: QrGeneratorState) => void;
  /** Suppress the inline action row at the bottom of the design panel. */
  hideInlineActions?: boolean;
  /**
   * Build an absolute URL pointing at the app host. Defaults to identity
   * so single-domain / same-origin call sites can omit the prop.
   */
  appUrl?: (path: string) => string;
}

export function QrGenerator({
  caps,
  seed,
  disableTypeChange = false,
  disablePayloadFields = false,
  disableEcc = false,
  previewPayloadOverride,
  actionSlot,
  onStateChange,
  hideInlineActions = false,
  appUrl = (path) => path,
}: QrGeneratorProps) {
  const t = useTranslations("generator");

  // --- Form + design state -------------------------------------------------
  const [type, setType] = useState<PayloadType>(seed?.type ?? "url");
  const [form, setForm] = useState<QrGeneratorFormState>(() => ({
    ...INITIAL_FORM,
    ...(seed?.form ?? {}),
  }));
  const [fg, setFg] = useState(seed?.foreground ?? "#000000");
  const [bg, setBg] = useState(seed?.background ?? "#FFFFFF");
  const [frame, setFrame] = useState<AnonymousFrame>(seed?.frame ?? "NONE");
  const [format, setFormat] = useState<AnonymousFormat>(seed?.format ?? "png");
  const [size, setSize] = useState<number>(seed?.size ?? 500);
  const [ecc, setEcc] = useState<AnonymousEcc>(seed?.ecc ?? "MEDIUM");
  const [displayMm, setDisplayMm] = useState<number>(100);
  const [watermarkRemoved, setWatermarkRemoved] = useState(false);
  const watermark = caps.allowWatermarkRemoval ? !watermarkRemoved : true;
  const [modePreference, setModePreference] = useState<QrMode>("DYNAMIC");
  const [minVersion, setMinVersion] = useState<number | null>(seed?.minVersion ?? null);
  const [preview, setPreview] = useState<string | null>(null);
  const [hasRendered, setHasRendered] = useState(false);
  const [vanityUpgradeOpen, setVanityUpgradeOpen] = useState(false);
  const [clientPreview, setClientPreview] = useState<string | null>(null);

  // Reset minVersion when capability downgrades
  useEffect(() => {
    if (!caps.allowMinVersion && minVersion !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- defensive reset on capability downgrade
      setMinVersion(null);
    }
  }, [caps.allowMinVersion, minVersion]);

  // --- Derived: matrix + ECC availability ---------------------------------
  const currentMatrixModules = useMemo(() => {
    const payload = buildPayload(type, form) ?? PLACEHOLDER_PAYLOAD;
    try {
      return computeMatrixModules(payload, ecc, minVersion ?? undefined);
    } catch {
      return 29;
    }
  }, [type, form, ecc, minVersion]);

  const matrixMeta = useMemo(() => {
    const payload = buildPayload(type, form) ?? PLACEHOLDER_PAYLOAD;
    try {
      return buildMatrixWithMeta(payload, ecc, minVersion ?? undefined);
    } catch {
      return null;
    }
  }, [type, form, ecc, minVersion]);

  const eccAvailability = useMemo<Record<AnonymousEcc, boolean>>(() => {
    const payload = buildPayload(type, form) ?? PLACEHOLDER_PAYLOAD;
    return {
      LOW: isEccUsable(payload, "LOW"),
      MEDIUM: isEccUsable(payload, "MEDIUM"),
      QUARTILE: isEccUsable(payload, "QUARTILE"),
      HIGH: isEccUsable(payload, "HIGH"),
    };
  }, [type, form]);

  useEffect(() => {
    if (eccAvailability[ecc]) return;
    const fallback: AnonymousEcc | undefined = (
      ["HIGH", "QUARTILE", "MEDIUM", "LOW"] as const
    ).find((e) => eccAvailability[e]);
    if (fallback && fallback !== ecc) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- depends on async payload-encoder check
      setEcc(fallback);
    }
  }, [eccAvailability, ecc]);

  // --- Resolution-slider bounds + auto-clamp ------------------------------
  const requiredMinPxForScan = Math.max(
    RENDER_SIZE_MIN_PX,
    currentMatrixModules * PX_PER_MODULE_GREEN,
  );
  const dpiCapMaxPx = Math.max(
    RENDER_SIZE_MIN_PX,
    Math.min(RENDER_SIZE_MAX_PX, Math.round((displayMm * caps.maxDpi) / MM_PER_INCH)),
  );
  const scanImpossibleWithoutPro = requiredMinPxForScan > dpiCapMaxPx;
  const dynamicMinPx = scanImpossibleWithoutPro ? dpiCapMaxPx : requiredMinPxForScan;
  const dynamicMaxPx = dpiCapMaxPx;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clamps user-set size into the new dynamic range
    setSize((prev) => Math.max(dynamicMinPx, Math.min(dynamicMaxPx, prev)));
  }, [dynamicMinPx, dynamicMaxPx]);

  // --- Vanity-stack guard --------------------------------------------------
  const vanityCount = countActiveVanityFeatures({
    foreground: fg,
    background: bg,
    frame,
    ecc,
  });
  const vanityLimit = caps.vanityStackLimit;
  const vanityAtLimit = vanityLimit !== null && vanityCount >= vanityLimit;

  const tryApplyVanity = (next: {
    fg?: string;
    bg?: string;
    frame?: AnonymousFrame;
    ecc?: AnonymousEcc;
  }): boolean => {
    const candidate = {
      foreground: next.fg ?? fg,
      background: next.bg ?? bg,
      frame: next.frame ?? frame,
      ecc: next.ecc ?? ecc,
    };
    const candidateCount = countActiveVanityFeatures(candidate);
    if (vanityLimit !== null && candidateCount > vanityLimit) {
      setVanityUpgradeOpen(true);
      return false;
    }
    if (next.fg !== undefined) setFg(next.fg);
    if (next.bg !== undefined) setBg(next.bg);
    if (next.frame !== undefined) setFrame(next.frame);
    if (next.ecc !== undefined) setEcc(next.ecc);
    return true;
  };

  // --- Form helpers --------------------------------------------------------
  const update = <K extends keyof QrGeneratorFormState>(
    key: K,
    value: QrGeneratorFormState[K],
  ) => setForm((s) => ({ ...s, [key]: value }));

  // --- Hidden compare-view + draft bridge ---------------------------------
  const compareMode = useSearchParams().get("compare") === "1";

  const persistDraftToStorage = () => {
    saveDraft({ type, form, foreground: fg, background: bg, frame, ecc, minVersion });
  };

  // --- Backend render (debounced) -----------------------------------------
  const mutation = useMutation({
    mutationFn: async (opts: { withBranding: boolean }) => {
      const payload = previewPayloadOverride ?? buildPayload(type, form);
      if (!payload) throw { status: 400, problem: { detail: t("error.invalidPayload") } };
      const res = await renderAnonymous({
        payload,
        design: { foreground: fg, background: bg, frame, ecc },
        format,
        withBranding: opts.withBranding,
        size,
      });
      if (!res.ok) throw res;
      return res;
    },
    onSuccess: (res) => {
      const objectUrl = URL.createObjectURL(res.bytes);
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
      setHasRendered(true);
    },
  });

  useEffect(() => {
    const payload = buildPayload(type, form);
    if (!payload) return;
    const handle = window.setTimeout(
      () => mutation.mutate({ withBranding: watermark }),
      400,
    );
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, form, fg, bg, frame, format, watermark, size, ecc]);

  // --- Client-side preview -------------------------------------------------
  useEffect(() => {
    const payload =
      previewPayloadOverride ?? (buildPayload(type, form) ?? PLACEHOLDER_PAYLOAD);
    let url: string | null = null;
    try {
      const result = renderClient({
        payload,
        design: { foreground: fg, background: bg, frame, ecc },
        format,
        withBranding: watermark,
        size,
        minVersion: minVersion ?? undefined,
      });
      if (result.ok) {
        url = URL.createObjectURL(result.bytes);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- blob-URL lifecycle needs effect + cleanup
        setClientPreview(url);
      } else {
        setClientPreview(null);
      }
    } catch {
      setClientPreview(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [type, form, fg, bg, frame, format, watermark, size, ecc, previewPayloadOverride, minVersion]);

  // --- Save-input + live validation ---------------------------------------
  const saveInput = useMemo(() => {
    const payload = buildPayload(type, form);
    if (!payload) return null;
    const result = buildSaveInput({
      payload,
      design: { foreground: fg, background: bg, frame, ecc, minVersion },
      forceMode: isDynamicCompatible(type) ? modePreference : undefined,
    });
    return result.ok ? result.value : null;
  }, [type, form, fg, bg, frame, ecc, modePreference, minVersion]);

  const liveValidation = useMemo(() => {
    const livePayload = buildPayload(type, form);
    if (!livePayload) return null;
    const result = AnonymousPayloadSchema.safeParse(livePayload);
    return result.success ? null : result.error;
  }, [type, form]);

  const liveValidationMessage = useMemo<string | null>(() => {
    if (!liveValidation || liveValidation.issues.length === 0) return null;
    const first = liveValidation.issues[0]!;
    const path = (first.path ?? [])
      .filter((p): p is string | number => typeof p === "string" || typeof p === "number")
      .join(".");
    const detail = path ? `${path}: ${first.message ?? ""}`.trim() : (first.message ?? "");
    return t("error.validationFailed", { detail });
  }, [liveValidation, t]);

  // --- Field validation + aria-invalid ------------------------------------
  const invalidFieldIds = useMemo<ReadonlySet<string>>(() => {
    const ids = new Set<string>();
    const collect = (
      issues: ReadonlyArray<{ path?: ReadonlyArray<PropertyKey> }> | undefined,
    ) => {
      if (!Array.isArray(issues)) return;
      for (const issue of issues) {
        const segs = ((issue.path ?? []) as ReadonlyArray<PropertyKey>).filter(
          (p: PropertyKey): p is string | number =>
            typeof p === "string" || typeof p === "number",
        );
        const id = zodPathToFieldId(type, segs);
        if (id) ids.add(id);
      }
    };
    const err = mutation.error as
      | { name?: string; issues?: ReadonlyArray<{ path?: ReadonlyArray<PropertyKey> }> }
      | null;
    if (err?.name === "ZodError") collect(err.issues);
    if (liveValidation) collect(liveValidation.issues);
    return ids;
  }, [mutation.error, liveValidation, type]);
  const ariaInvalid = (id: string): true | undefined =>
    invalidFieldIds.has(id) ? true : undefined;

  // The effective mode published in QrGeneratorState. The Code-Modus toggle
  // is hidden for payload types that can't round-trip through the redirect
  // hotpath (everything but URL/FILE — see IncompatibleDynamicPayloadException
  // server-side); in that case the user has no way to pick DYNAMIC, so the
  // snapshot collapses to STATIC. Save-action consumers feed this into
  // `forceMode` so the wire `mode` matches what the user actually picked.
  const effectiveMode: QrMode = isDynamicCompatible(type) ? modePreference : "STATIC";

  // --- onStateChange notifier ---------------------------------------------
  useEffect(() => {
    if (!onStateChange) return;
    onStateChange({
      type,
      form,
      payload: buildPayload(type, form),
      design: { foreground: fg, background: bg, frame, ecc, minVersion, format, size },
      validationOk: liveValidation === null,
      validationMessage: liveValidationMessage,
      mode: effectiveMode,
    });
  }, [
    type,
    form,
    fg,
    bg,
    frame,
    ecc,
    minVersion,
    format,
    size,
    liveValidation,
    liveValidationMessage,
    onStateChange,
    effectiveMode,
  ]);

  const errMessage = useErrMessage({
    mutationError: mutation.error,
    liveValidation,
    vanityUpgradeOpen,
    onOpenVanityUpgrade: () => setVanityUpgradeOpen(true),
  });

  // --- Action-bar handlers (clipboard / share / print / download) --------
  const fetchCurrentBlob = async (): Promise<Blob | null> => {
    if (mutation.data?.bytes instanceof Blob) return mutation.data.bytes;
    if (clientPreview) {
      try {
        const res = await fetch(clientPreview);
        return await res.blob();
      } catch {
        return null;
      }
    }
    return null;
  };

  const { toast, mounted, canShareFiles, onCopyImage, onCopyHtml, onShare, onPrint } =
    useActionHandlers({ fetchCurrentBlob, format, displayMm, size });

  const onDownload = () =>
    mutation.mutate(
      { withBranding: watermark },
      { onSuccess: (res) => downloadBlob(res.bytes, `qrody-qr.${format}`) },
    );

  // --- Action-state snapshot for actionSlot consumers ---------------------
  const actionState: QrGeneratorActionState = {
    type,
    form,
    payload: buildPayload(type, form),
    design: { foreground: fg, background: bg, frame, ecc, minVersion, format, size },
    validationOk: liveValidation === null,
    validationMessage: liveValidationMessage,
    mode: effectiveMode,
  };

  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate({ withBranding: watermark });
        }}
        className="space-y-3"
      >
        {/* Main 3-column layout. Mobile-first: stacks Inhalt → QR →
            Gestaltung. From `lg` onwards the QR is the central column
            (1.6fr) flanked by config (1fr each).
            Note: the previous `minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)`
            arbitrary value didn't survive Tailwind v4's arbitrary-value
            parser when nested with commas — the class disappeared from
            the generated CSS, the layout silently fell back to the
            mobile single-column stack. Plain `1fr` works fine because
            PreviewPanel carries `min-w-0` already, and the other two
            panels don't host overflowing children. */}
        <div className="grid gap-3 md:grid-cols-2 md:items-start lg:grid-cols-[1fr_1.6fr_1fr]">
          <ContentPanel
            type={type}
            setType={setType}
            form={form}
            update={update}
            ariaInvalid={ariaInvalid}
            caps={caps}
            disableTypeChange={disableTypeChange}
            disablePayloadFields={disablePayloadFields}
            modePreference={modePreference}
            setModePreference={setModePreference}
            hasRendered={hasRendered}
            appUrl={appUrl}
            persistDraftToStorage={persistDraftToStorage}
          />
          <PreviewPanel
            compareMode={compareMode}
            clientPreview={clientPreview}
            serverPreview={preview}
            format={format}
            pending={mutation.isPending}
            errMessage={errMessage}
            displayMm={displayMm}
            setDisplayMm={setDisplayMm}
            matrixMeta={matrixMeta}
            currentMatrixModules={currentMatrixModules}
            ecc={ecc}
          />
          <DesignPanel
            caps={caps}
            displayMm={displayMm}
            size={size}
            setSize={setSize}
            currentMatrixModules={currentMatrixModules}
            requiredMinPxForScan={requiredMinPxForScan}
            dpiCapMaxPx={dpiCapMaxPx}
            scanImpossibleWithoutPro={scanImpossibleWithoutPro}
            dynamicMinPx={dynamicMinPx}
            dynamicMaxPx={dynamicMaxPx}
            fg={fg}
            bg={bg}
            frame={frame}
            ecc={ecc}
            eccAvailability={eccAvailability}
            disableEcc={disableEcc}
            tryApplyVanity={tryApplyVanity}
            vanityCount={vanityCount}
            vanityLimit={vanityLimit}
            vanityAtLimit={vanityAtLimit}
            minVersion={minVersion}
            setMinVersion={setMinVersion}
            setVanityUpgradeOpen={setVanityUpgradeOpen}
            watermarkRemoved={watermarkRemoved}
            setWatermarkRemoved={setWatermarkRemoved}
            format={format}
            setFormat={setFormat}
            mounted={mounted}
            hideInlineActions={hideInlineActions}
            actionSlot={actionSlot}
            actionState={actionState}
            saveInput={saveInput}
            pending={mutation.isPending}
            liveValidationOk={liveValidation === null}
            hasRendered={hasRendered}
            canShareFiles={canShareFiles}
            errMessage={errMessage}
            onDownload={onDownload}
            onCopyImage={onCopyImage}
            onCopyHtml={onCopyHtml}
            onShare={onShare}
            onPrint={onPrint}
          />
        </div>
      </form>

      {/* Toast für Copy/Share/Print-Feedback. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background shadow-lg"
        >
          {toast}
        </div>
      )}

      {/* Print-Target — versteckt im normalen Flow, sichtbar via @media print. */}
      <div id="qr-print-root" aria-hidden>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img id="qr-print-target" alt="" />
      </div>

      <VanityUpgradeDialog
        open={vanityUpgradeOpen}
        onClose={() => setVanityUpgradeOpen(false)}
        appUrl={appUrl}
        persistDraftToStorage={persistDraftToStorage}
      />
    </div>
  );
}

